// date-display — relative/absolute date formatting. Project convention:
// "Dates: relative if recent, absolute otherwise."
//
// ⚠️ AMENDED 2026-10-09 (final fix wave M-10). ORIGINAL (verbatim): "relative/
// absolute date formatting for account-section surfaces (subscription
// renewal/effective/expiry dates)". CURRENT: the subscription surfaces are
// gone; the ONLY consumer is the passkey created/last-used timestamps in
// SecurityPanel (re-derived by `command grep -rl date-display src`). It is
// also a near-twin of formatRelativeCalendarDate in embed-shared/format.ts — merging the two
// is filed as TD-598; until then this file is the passkey timestamps' helper
// and nothing else should grow a dependency on it.

const RECENT_DAY_THRESHOLD = 6;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Format an ISO-8601 date/time string as a relative phrase ("Today",
 * "in 3 days", "5 days ago") when within +/- RECENT_DAY_THRESHOLD whole days
 * of `now`, else an absolute locale date ("Aug 20, 2026").
 *
 * Returns "—" (project convention: empty values render em dash) for an
 * unparseable input — this function never guesses or fabricates a date.
 */
export function formatRelativeOrAbsoluteDate(
  iso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!iso) return "—";
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return "—";

  const diffDays = Math.round((target.getTime() - now.getTime()) / MS_PER_DAY);
  if (Math.abs(diffDays) <= RECENT_DAY_THRESHOLD) {
    if (diffDays === 0) return "Today";
    const rtf = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
    return rtf.format(diffDays, "day");
  }
  return target.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}
